import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BASE_URL } from "../utilities/BASE_URL";
import { fetchWithToken, postWithToken } from "../utilities/fetchWithToken";
import { googleLoginUser, loginUser, logout } from "./authSlice";


// Quick-select bet presets above the action bar. preflop = big-blind multiples,
// postflop = % of pot. Mirrors the backend defaults.
export const DEFAULT_BET_SETTINGS = { preflop: [2, 3, 4, 5], postflop: [33, 50, 75, 100] };

export const getBetSettings = createAsyncThunk(
    "playerInfo/getBetSettings",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchWithToken(`${BASE_URL}/playerInfo/betSettings`);
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch bet settings");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const saveBetSettings = createAsyncThunk(
    "playerInfo/saveBetSettings",
    async (settings, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/playerInfo/betSettings`, settings);
            const data = await response.json();
            if (!response.ok) {
                return rejectWithValue(data.error || "Failed to save bet settings");
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Profile picture id (Customize → Profile Picture). null = default "P" circle.
export const getAvatar = createAsyncThunk(
    "playerInfo/getAvatar",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchWithToken(`${BASE_URL}/playerInfo/avatar`);
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch avatar");
            }
            const data = await response.json();
            return data.avatar ?? null;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const saveAvatar = createAsyncThunk(
    "playerInfo/saveAvatar",
    async (avatar, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/playerInfo/avatar`, { avatar });
            const data = await response.json();
            if (!response.ok) {
                return rejectWithValue(data.error || "Failed to save avatar");
            }
            return data.avatar ?? null;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getPlayerChips = createAsyncThunk(
    "playerInfo/getchips",
    async(_, {rejectWithValue, dispatch}) => {
        try{
            const response = await fetchWithToken(`${BASE_URL}/playerInfo/chips`);

            if(!response.ok) {
                // Backend rejected our token (expired/bad signature/unknown user).
                // It's the authority on auth, so drop the token; ProtectedRoute then
                // bounces us to the login screen.
                if (response.status === 401) {
                    dispatch(logout());
                }
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch your chips");
            }

            const data = await response.json();
            return data;

        } catch (error) {
            return rejectWithValue(error.message);

        }
    }
    

)


const initialState = {
    chips: 0,
    username: null,
    betSettings: DEFAULT_BET_SETTINGS,
    avatar: null,
};

const playerInfoSlice = createSlice(
    {
        name: "playerInfo",
        initialState,
        reducers: {
            // You can add synchronous reducers here if needed.
        },
        extraReducers: (builder) => {
            builder
                .addCase(getPlayerChips.pending, (state) => {
                    state.loading = true;
                    state.error = null;
                })
                .addCase(getPlayerChips.fulfilled, (state, action) => {
                    state.loading = false;
                    state.chips = action.payload.chips;
                    state.username = action.payload.username;
                })
                .addCase(getPlayerChips.rejected, (state, action) => {
                    state.loading = false;
                    state.error = action.payload || "Something went wrong";
                })
                .addCase(getBetSettings.fulfilled, (state, action) => {
                    state.betSettings = action.payload;
                })
                .addCase(saveBetSettings.fulfilled, (state, action) => {
                    state.betSettings = action.payload;
                })
                .addCase(getAvatar.fulfilled, (state, action) => {
                    state.avatar = action.payload;
                })
                .addCase(saveAvatar.fulfilled, (state, action) => {
                    state.avatar = action.payload;
                })
                .addCase(loginUser.fulfilled, () => initialState)
                .addCase(googleLoginUser.fulfilled, () => initialState)
                .addCase(logout, () => initialState)
                // Table money moves the ACCOUNT balance, so the top bar must follow it without
                // waiting to be remounted. Leaving a cash table cashes the stack out to the
                // wallet and joining one debits the buy-in; both responses carry the resulting
                // balance, which the server computed under the game-row lock. Matched by action
                // type rather than by importing gameSlice's thunks, to keep the slices acyclic.
                // (Kept after every addCase — RTK requires matchers last.)
                .addMatcher(
                    (action) => action.type === "game/leaveTable/fulfilled"
                        || action.type === "game/standUp/fulfilled"
                        || action.type === "game/joinGame/fulfilled",
                    (state, action) => {
                        // A DEFERRED leave answers before the cash-out runs (the engine settles
                        // the seat after the pot is awarded), so its `chips` is the pre-payout
                        // balance — writing it would show a stale total. TopMenu's mount refetch
                        // is the authority for that case.
                        if (action.payload?.deferred === true) return;
                        const chips = action.payload?.chips;
                        if (typeof chips === "number" && Number.isFinite(chips)) {
                            state.chips = chips;
                        }
                    }
                );
        }


    }
)

export default playerInfoSlice.reducer;
